import { Component, OnInit } from '@angular/core';
import { UntypedFormControl } from "@angular/forms";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { MatDialog } from "@angular/material/dialog";
import { PageEvent } from "@angular/material/paginator";
import { Sort } from '@angular/material/sort';
import { AlertService } from "src/app/share/service/alert.service";
import { debounceTime, distinctUntilChanged } from 'rxjs';
import {
  ExternalVoucherListingRequestInterface,
  ExternalVoucherListingResultInterface
} from "src/app/vouchers/_interfaces/externalVouchers.interface";
import { FileUploaderPopupComponent } from 'src/app/share/component/file-uploader-popup/file-uploader-popup.component';
import { VouchersDataService } from '../_services/vouchers-data.service';
import { triggerFileDownload } from "src/app/share/helpers/downloadFile.helper";
import { ConfirmationDialog } from "src/app/share/dialog/confirmation-dialog/confirmation.dialog";

@Component({
  selector: 'app-external-vouchers',
  templateUrl: './external-vouchers.component.html',
  styleUrls: ['./external-vouchers.component.scss']
})
export class ExternalVouchersComponent implements OnInit {

  //#region Properties
  selectedVoucher: ExternalVoucherListingResultInterface;

  /* Filter Section variables */
  searchText: UntypedFormControl;

  availableVoucherStatuses: string[] = [];
  filteredVoucherStatuses: string[] = this.availableVoucherStatuses;
  selectedVouchersStatuses: string[] = this.availableVoucherStatuses;

  /* Listing Section variables */
  list: ExternalVoucherListingResultInterface[] = [];
  length = 0;
  displayedColumns: string[] = ["createdDate", "voucherPIN", "expiryDate", "status", "vehicleNo", "action"];
  sortIndex: number = 0;
  sortDirection: "asc" | "desc" = "desc";
  limit: number = 50;
  offset: number = 1;

  //#endregion

  constructor(
    private alertService: AlertService,
    private bottomSheet: MatBottomSheet,
    private vouchersDataService: VouchersDataService,
    public dialog: MatDialog,
  ) {
    this.searchText = new UntypedFormControl(null);
  }

  //#region Lifecycles

  ngOnInit(): void {
    // NOTE: subscribe searchText's valueChanges event to trigger auto-search after 1s of text input
    this.searchText.valueChanges.pipe(debounceTime(1000), distinctUntilChanged()).subscribe(() => {
      this.getListingData();
    })

    // NOTE: initial listing retrieval
    this.getListingData(true);
  }

  //#endregion

  //#region Event Handlers

  onTextFilterClear(): void {
    this.searchText.reset();
  }

  onSelectionFilterOptionChange(): void {
    this.getListingData();
  }

  onSelectSearchChange(searchValue: string = '', key: string): void {
    const value = searchValue.trim().toLowerCase();
    const availableOptions = this.getOptionsBundleByKey[key].availableOptions;
    const filteredOptionsFunc = this.getFilteredOptionsFuncByKey[key];

    if (!availableOptions || !filteredOptionsFunc)
      return;

    if (value) {
      filteredOptionsFunc(availableOptions.filter(x => x.toString().toLowerCase().includes(value)));
    } else {
      filteredOptionsFunc([...availableOptions]);
    }
  }

  onResetFilteredOption(event: MouseEvent, key: string): void {
    event.stopPropagation();

    const resetSelectedOptions = this.getResetSelectedOptionsFuncByKey[key];

    // NOTE: execute the function if found to reset the selectedXXX options list
    resetSelectedOptions?.();
    this.getListingData();
  }

  onDownloadTemplateClick() {
    this.downloadExcelTemplate();
  }

  onUploadExcelClick() {
    const bottomRef = this.bottomSheet.open(FileUploaderPopupComponent, {
      backdropClass: "sidenav-container",
      disableClose: true,
      data: {
        title: "Upload External Vouchers",
        serviceType: "External Vouchers",
        isAllowExcel: true
      }
    });
    bottomRef.afterDismissed().subscribe((result) => {
      if (result && result.success) {
        this.getListingData(true);
      }
    });
  }

  onExportListingClick() {
    this.exportListingData()
  }

  onItemDeactivateClick(event: MouseEvent, element: ExternalVoucherListingResultInterface) {
    event.stopPropagation();

    const dialogRef = this.dialog.open(
      ConfirmationDialog,
      {
        width: "400px",
        data: {
          title: "Deactivate " + element.voucherPIN,
          content: "",
          multiLineContent: [
            "Are you sure you want to **red:deactivate this active voucher code**?",
            "This action cannot be undone and may affect ongoing disbursements."
          ],
          btnColor: "primary",
          okBtnText: "Confirm",
          boldContent: "",
          hideQuestionMark: true
        }
      }
    );

    dialogRef.afterClosed().subscribe(x => {
      if (x) {
        this.selectedVoucher = element;
        this.deactivateVoucher();
      }
    });
  }

  onBulkDeactivateClick() {
    const dialogRef = this.dialog.open(
      ConfirmationDialog,
      {
        width: "400px",
        data: {
          title: "Bulk Deactivate",
          content: "",
          multiLineContent: [
            "Are you sure you want to **red:deactivate all active voucher code**?",
            "This action cannot be undone and may affect ongoing disbursements."
          ],
          btnColor: "primary",
          okBtnText: "Confirm",
          boldContent: "",
          hideQuestionMark: true
        }
      }
    );

    dialogRef.afterClosed().subscribe(x => {
      if (x) {
        this.selectedVoucher = null;
        this.deactivateVoucher();
      }
    });
  }

  onSortChange(sortState: Sort) {
    this.sortDirection = null;
    this.sortIndex = -1;

    if (sortState.direction) {
      this.sortDirection = sortState.direction;
      this.sortIndex = this.displayedColumns.findIndex(
        (x) => x == sortState.active
      );
    }

    if (this.list.length == 0) return;

    this.getListingData();
  }

  onPageChange(e: PageEvent) {
    this.offset = e.pageIndex + 1;
    this.limit = e.pageSize;

    this.getListingData(false, false);
  }

  //#endregion

  //#region Public Methods

  getMultiFilterLabel(key: string) {
    const availableOptions = this.getOptionsBundleByKey[key].availableOptions;
    const selectedOptions = this.getOptionsBundleByKey[key].selectedOptions;
    const optionsSelectAllLabel = this.getOptionsSelectAllLabelByKey[key];

    if (!selectedOptions || !availableOptions)
      return null;

    // NOTE: If only 1 item selected, the label will be the selected item's value
    if (selectedOptions.length === 1)
      return selectedOptions[0];

    // NOTE:
    //  - If selected > 1 and < all options, the label will be the number of selected items
    //  - If selected all options, the label will be All XXX based on the key
    return selectedOptions.length === availableOptions.length ? optionsSelectAllLabel : `${selectedOptions.length}`;
  }

  toggleSelectAll(isSelectAll: boolean, key: string): void {
    const optionsBundle = this.getOptionsBundleByKey[key];
    const patchSelectedOptionsFunc = this.getPatchSelectedOptionsFuncByKey[key];

    if (!optionsBundle || !patchSelectedOptionsFunc)
      return;

    // NOTE:
    //  - If toggled on Select All, the source will be availableXXX
    //  - If toggled off Select All, the source will be empty (clear selectedXXX)
    optionsBundle.selectedOptions = (isSelectAll) ? [...optionsBundle.availableOptions] : [];

    // NOTE: execute the function if found to patch the selectedXXX options list accordingly
    patchSelectedOptionsFunc?.(optionsBundle.selectedOptions);

    this.getListingData();
  }

  isSelectedAll(key: string): boolean {
    const optionsBundle = this.getOptionsBundleByKey[key];

    return optionsBundle ? optionsBundle.selectedOptions.length === optionsBundle.availableOptions.length : false;
  }

  isSelectedOptionsIndeterminate(key: string): boolean {
    const optionsBundle = this.getOptionsBundleByKey[key];

    if (!optionsBundle) return false;

    return optionsBundle.selectedOptions.length > 0 && optionsBundle.selectedOptions.length < optionsBundle.availableOptions.length;
  }

  //#endregion

  //#region Private Methods

  private getListingData(isInitiateFilter: boolean = false, isResetOffset: boolean = true) {
    this.list = [];
    this.length = 0;
    this.offset = (isResetOffset) ? 1 : this.offset;

    // NOTE: when executed with isInitiateFilter = true, clear the filterings before retrieval
    //  - only during initial retrieval, after successful upload retrieval, after successful deactivation retrieval
    if (isInitiateFilter)
      this.clearFilterings();

    let request: ExternalVoucherListingRequestInterface = {
      text: this.searchText?.value || null,
      selectedStatuses: this.selectedVouchersStatuses,
      columnIndex: this.sortIndex,
      sortDirection: this.sortDirection,
      limit: this.limit,
      offset: this.offset
    };

    this.vouchersDataService.getExternalVoucherListing(request).subscribe({
      next: (x) => {
        this.length = x.count;
        this.list = x.data;

        // NOTE: when executed with isInitiateFilter = true, reset the selection box filterings with the available option items
        //  - only during initial retrieval, after successful upload retrieval, after successful deactivation retrieval
        if (isInitiateFilter) {
          this.populateStatusFilterOptions(x.statusFilterOptions);
        }
      },
      error: (err) => {
        this.alertService.openSnackBar(err.error);
      }
    });
  }

  private exportListingData() {
    let request: ExternalVoucherListingRequestInterface = {
      text: this.searchText?.value || null,
      selectedStatuses: this.selectedVouchersStatuses,
      columnIndex: this.sortIndex,
      sortDirection: this.sortDirection,
      limit: this.limit,
      offset: this.offset
    };

    this.vouchersDataService.exportExternalVoucherListingExcel(request).subscribe(
      (blob) => {
        let today = new Date();
        triggerFileDownload(blob, `External Voucher Listing ${today.getDate().toString().padStart(2, '0')}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getFullYear()}.xlsx`);
      },
      (err) => {
        this.alertService.openSnackBar(`Error: ${err.message}`);
      }
    );
  }

  private downloadExcelTemplate() {
    this.vouchersDataService.downloadExternalVoucherExcelTemplate().subscribe(
      (blob) => {
        triggerFileDownload(blob, "External Voucher Excel Template.xlsx");
      },
      (err) => {
        this.alertService.openSnackBar(`Error: ${err.message}`);
      }
    );
  }

  private deactivateVoucher() {
    // NOTE: deactivateExternalVoucher behave based on selectedVoucher
    //  - selectedVoucher != null: individual deactivation process
    //  - selectedVoucher == null: bulk deactivation process
    this.vouchersDataService.deactivateExternalVoucher(this.selectedVoucher?.id || null).subscribe({
      next: (x) => {
        this.alertService.openSnackBar(x.message);

        // Only refresh listing upon successful deactivation.
        if (x.isSuccess)
          this.getListingData(true);
      },
      error: (err) => {
        this.alertService.openSnackBar(err.error);
      }
    });
  }

  private clearFilterings() {
    // NOTE: when reset searchText, due to the subscribed valueChanges observable in ngOnInit lifecycle, it will trigger the GetListing()
    //  - in order to skip triggering the observable, pass in emitEvent: false to the reset() function
    //  - otherwise getListingData(isInitiateFilter = true) will get triggered twice
    this.searchText.reset(null, { emitEvent: false });;
    this.selectedVouchersStatuses = []
  }

  private populateStatusFilterOptions(source: string[]) {
    this.availableVoucherStatuses = Array.from(
      new Set(source.map(item => item))
    );

    this.selectedVouchersStatuses = this.availableVoucherStatuses;
    this.filteredVoucherStatuses = this.availableVoucherStatuses;
  }

  /**
   * Return the respective key's options list bundle (included availableXXX, filteredXXX, selectedXXX options lists).
   */
  private get getOptionsBundleByKey(): Record<string, { availableOptions: any[], filteredOptions: any[], selectedOptions: any[] }> {
    return {
      voucherStatus: {
        availableOptions: this.availableVoucherStatuses,
        filteredOptions: this.filteredVoucherStatuses,
        selectedOptions: this.selectedVouchersStatuses,
      },
    };
  }

  /**
   * Return the respective label (string) for option's select all state by key.
   */
  private get getOptionsSelectAllLabelByKey(): Record<string, string> {
    return {
      voucherStatus: "All Statuses"
    };
  }

  /**
   * Return a function for the respective key's selectedXXX options list to patch the selectedXXX options list with provided value (val).
   */
  private get getPatchSelectedOptionsFuncByKey(): Record<string, (val: any[]) => void> {
    return {
      voucherStatus: val => this.selectedVouchersStatuses = val
    };
  }

  /**
   * Return a function for the respective key's filteredXXX options list to patch the filteredXXX options list with provided value (val).
   */
  private get getFilteredOptionsFuncByKey(): Record<string, (val: any[]) => void> {
    return {
      voucherStatus: val => this.filteredVoucherStatuses = val
    };
  }

  /**
   * Return a function for the respective key's selectedXXX options list to reset selectedXXX options list with respective availableXXX options list.
   */
  private get getResetSelectedOptionsFuncByKey(): Record<string, () => void> {
    return {
      voucherStatus: () => this.selectedVouchersStatuses = [...this.availableVoucherStatuses]
    };
  }

  //#endregion

}
